import { QueryClient, useInfiniteQuery } from "@tanstack/react-query";
import { useAtomValue } from "jotai";

import BrieConversationAPI from "../../../api/api/BrieConversationAPI";
import { BrieGPTMessagePayload } from "./PageViewBrieGPT";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";

type ViewProps = {
  type: "view";
  viewID: string;
  chatID: string | null;
  processing: boolean;
};

type RecordProps = {
  type: "record";
  recordID: string | undefined;
  chatID: string | null;
  processing: boolean;
};

export type GetGPTMessagesProps = ViewProps | RecordProps;

const emptyArray: [] = [];

function useChatMessages(props: GetGPTMessagesProps) {
  const tenantID = useAtomValue(tenantIDAtom);

  let queryIds: string[];

  if (props.type === "view") {
    queryIds = [props.viewID];
  } else {
    queryIds = [props.recordID || ""];
  }

  const messages = useInfiniteQuery({
    initialPageParam: 1,
    queryKey: [tenantID, ...queryIds, props.chatID, "BrieGPTMessages"],
    select: (data) => ({
      pages: [...data?.pages].reverse(),
      pageParams: [...data?.pageParams].reverse(),
    }),
    queryFn: ({ pageParam = 1 }: { pageParam: number }) => {
      return BrieConversationAPI.getBrieGPTChatMessages({
        ...props,
        page: pageParam,
      }).then((res) => {
        const messages = res?.data?.data?.messages;
        const pagination = res?.data?.data?.pagination;

        return {
          messages: (messages || emptyArray) as BrieGPTMessagePayload[],
          pagination: {
            PreviousPage: pagination.PreviousPage ?? false,
            HasNextPage: pagination.HasNextPage ?? 0,
            NextPage: pagination.NextPage ?? 0,
          },
        };
      });
    },
    getNextPageParam: (lastPage) => {
      return lastPage?.pagination?.HasNextPage
        ? lastPage?.pagination?.NextPage
        : undefined;
    },
    enabled:
      props.type === "view"
        ? !!props.viewID && !!props.chatID
        : !!props.recordID && !!props.chatID,
  });

  function handleMessageUpdate(
    message: BrieGPTMessagePayload,
    client: QueryClient,
    queryKey: (string | null)[]
  ) {
    client.setQueryData(
      queryKey,
      (prev: {
        pageParams: number[];
        pages: {
          messages: BrieGPTMessagePayload[];
          pagination: {
            PreviousPage: boolean;
            HasNextPage: boolean;
            NextPage: number;
          };
        }[];
      }) => {
        // For new conversations.
        if (!prev) {
          return {
            pages: [
              {
                messages: [message],
                pagination: {
                  PreviousPage: false,
                  HasNextPage: false,
                  NextPage: 2,
                },
              },
            ],
            pageParams: [1],
          };
        }

        const pages = [...prev.pages];
        const pageParams = [...prev.pageParams];
        const firstMessagePage = pages[0];

        if (firstMessagePage.messages?.length < 15) {
          // Can be added to the existing page.
          firstMessagePage.messages.push(message);
        } else {
          // Need to create a new page.
          const newPage = {
            messages: [message],
            pagination: {
              PreviousPage: false,
              HasNextPage: true,
              NextPage: 2,
            },
          };

          pages.unshift(newPage);
          pageParams.push(pageParams?.length + 1);
        }

        return {
          pages,
          pageParams,
        };
      }
    );
  }

  return {
    messagesPages: messages.data?.pages || emptyArray,
    messagesLoading: messages.isLoading && props.chatID,
    fetching: messages.isFetching,
    error: messages.error,
    hasNextPage: messages?.hasNextPage,
    fetchNextPage: messages.fetchNextPage,
    handleMessageUpdate,
  };
}

export default useChatMessages;
